import React, { useCallback, useState, useRef } from "react";
import { v4 as uuid } from "uuid";
import CheckBoxLabel from "./CheckBoxLabel";

const useCheckboxGroup = ({ tableRef: _tr }) => {
	const mainCheckRef = useRef(null);
	const checkedRef = useRef(new Set()); // 暫存被勾選的對象，實則沒有意義只是用來UI顯示
	const [commonNameID] = useState(uuid());
	const [mainUID, setMainUID] = useState(uuid()); // 刷新子組建checkbox用，此方法為HOC
	const masterID = `${commonNameID}-main`;
	const checkboxCommonName = `input[type="checkbox"][data-name="${commonNameID}"]`;
	const checkboxMasterName = `input[type="checkbox"][data-name="${masterID}"]`;

	const getElementStatus = useCallback(
		() => {
			let c = false;
			let d = false;
			const nodes = _tr.current.querySelectorAll(checkboxCommonName);
			const arr = Array.from(nodes);
			if (arr.length > 0) {
				const f = arr.filter(({ checked }) => checked);
				c = arr.length === f.length;
				d = f.length > 0;
			}
			return [c, d];
		},
		// eslint-disable-next-line
		[]
	);

	const onUpdateCheckBox = useCallback(
		() => {
			const nodes = _tr.current.querySelectorAll(checkboxCommonName);
			const arr = Array.from(nodes);
			if (arr.every(({ checked }) => checked)) {
				mainCheckRef.current.setChecked(true);
				mainCheckRef.current.indeterminateRef(false);
			} else if (arr.every(({ checked }) => !checked)) {
				mainCheckRef.current.setChecked(false);
				mainCheckRef.current.indeterminateRef(false);
			} else {
				mainCheckRef.current.setChecked(false);
				mainCheckRef.current.indeterminateRef(true);
			}
		},
		// eslint-disable-next-line
		[]
	);

	const onResetCheckBox = useCallback(
		() => {
			if (mainCheckRef.current) {
				mainCheckRef.current.setChecked(false);
				mainCheckRef.current.indeterminateRef(false);
				checkedRef.current = new Set();
				setMainUID(uuid());
			}
		},
		// eslint-disable-next-line
		[]
	);

	// 只回傳 定義上的id 如需其他類型參數，該呼叫此方法的自行在外面重組
	const getResult = useCallback(
		() => {
			const nodes = _tr.current.querySelectorAll(checkboxCommonName);
			const getCheckedNodes = Array.from(nodes).filter(({ checked }) => checked);
			const ids = getCheckedNodes.map(({ dataset }) => dataset.value);
			return ids.map(Number);
		},
		// eslint-disable-next-line
		[]
	);

	// 全選按鈕
	const handlePutCheckedAll = useCallback(
		(_v, _c) => {
			const nodes = _tr.current.querySelectorAll(checkboxCommonName);
			const node = document.querySelector(checkboxMasterName);
			if (nodes.length === 0) return;
			if (node.checked) {
				const arr = Array.from(nodes).map(({ dataset }) => dataset.value);
				checkedRef.current = new Set(arr);
				mainCheckRef.current.indeterminateRef(false);
			} else {
				checkedRef.current = new Set();
			}
			setMainUID(uuid());
		},
		// eslint-disable-next-line
		[]
	);

	// 所以單選事件源頭
	const handleSingleOnChange = useCallback(
		(_v, _c) => {
			const target = checkedRef.current;
			const [c, d] = getElementStatus();
			if (_c) {
				target.add(String(_v));
			} else {
				target.delete(String(_v));
			}
			if (!c && !d) {
				mainCheckRef.current.indeterminateRef(false);
				mainCheckRef.current.setChecked(false);
			} else {
				mainCheckRef.current.setChecked(c && d);
				mainCheckRef.current.indeterminateRef(!c && d);
			}
			if (c && d) {
				setMainUID(uuid());
			}
		},
		// eslint-disable-next-line
		[]
	);

	// 各個欄位單選使用元件
	const Checkbox = useCallback(
		({ value }) => {
			const target = checkedRef.current;
			const defaultChecked = target.has(String(value));
			return <CheckBoxLabel value={value} elementName={commonNameID} onChange={handleSingleOnChange} defaultChecked={defaultChecked} />;
		},
		// eslint-disable-next-line
		[handleSingleOnChange, mainUID]
	);

	// 單一 全選/取消 使用元件
	const CheckboxAll = useCallback(
		() => {
			return <CheckBoxLabel ref={mainCheckRef} elementName={masterID} onChange={handlePutCheckedAll} />;
		},
		// eslint-disable-next-line
		[handleSingleOnChange]
	);

	return { getResult, Checkbox, CheckboxAll, onResetCheckBox, onUpdateCheckBox };
};

export default useCheckboxGroup;
